import { useContext, useEffect } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { capitalizeString, digObject } from '@domain-group/javascript-utils'

import useTrainingCourse, { useTrainingCourseForm } from '@hooks/useTrainingCourse'

import EntitySelector from '@components/EntitySelector'

import { mapEntityTypeToEntityKey } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { TrainingCourseModel } from '@models/types'

const MODAL_KEY = 'CreateOrDuplicateTrainingCourseModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTrainingCourse?: (payload: HandleCallbackActionParams) => void,
  duplicateTrainingCourse?: (payload: HandleCallbackActionParams) => void,
}

type CreateOrDuplicateTrainingCoursePayload = {
  callbacks: ModalCallbacks,
  createFn: Function,
  entityState: Partial<TrainingCourseModel>,
  duplicateFn: Function,
}

const createOrDuplicateTrainingCourse = (params: CreateOrDuplicateTrainingCoursePayload) => {
  const {
    callbacks, createFn, duplicateFn, entityState,
  } = params

  const { closeModal, createTrainingCourse, duplicateTrainingCourse } = callbacks

  const [text, actionFn] = entityState.id ? ['Duplicated', duplicateFn] : ['Created', createFn]

  const trainingCoursePayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    toastText: `Training Course ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return duplicateTrainingCourse!(trainingCoursePayload)

  // @ts-expect-error TS2345
  return createTrainingCourse!(trainingCoursePayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrDuplicateTrainingCourseModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { trainingCourse: initTrainingCourse } = modalPayload

  const {
    callbacks: {
      createTrainingCourse: createFn,
      duplicateTrainingCourse: duplicateFn,
    },
    creating,
    trainingCourse,
  } = useTrainingCourse(initTrainingCourse)

  const {
    entityState,
    entityState: {
      audience_id,
      audience_type,
      id,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  // @ts-expect-error TS2345
  } = useTrainingCourseForm(trainingCourse, { validateOn: 'change' })

  const createOrDuplicateTrainingCoursePayload = {
    callbacks,
    createFn,
    duplicateFn,
    entityState,
  }

  const entityKey = mapEntityTypeToEntityKey(audience_type)

  const buttonText = id ? 'Duplicate Training Course' : 'Create Training Course'

  useEffect(() => {
    if (id) setEntityState({ title: `${trainingCourse.title} Copy` })
  }, [id])

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Duplicate' : 'Create'}
        titleSecondLine="Training Course"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Audience" errorMessage={errors.audience_type} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="audience_type" value={audience_type} {...handlers}>
              <option value="">Please Select...</option>
              <option value="System">System</option>
              <option value="Agency">Agencies</option>
              <option value="Brand">Brands</option>
              <option value="Provider">Providers</option>
            </select>
          </Form.Field>

          {!!audience_type && audience_type !== 'System' && (
            <EntitySelector
              callbacks={{
                // @ts-expect-error TS2322
                selectItem: (entity: { id?: number }) => setEntityState(
                  { audience_id: entity ? entity.id : null },
                ),
              }}
              entityKey={entityKey}
              errorMessage={errors.audience_id}
              marginTop="large"
              searchForwardProps={{ placeholder: `All ${capitalizeString(entityKey)}` }}
              selectedItemId={audience_id}
            />
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createOrDuplicateTrainingCourse(createOrDuplicateTrainingCoursePayload)}
          size="large"
        >
          {saveEnabled ? buttonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrDuplicateTrainingCourseModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
